import { Component } from '@theme/component';

/**
 * @typedef {Object} FeaturedProductsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} tabLabels
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<FeaturedProductsRefs>} */
export class FeaturedProductsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  #active = 0;

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#syncTabLabels();
  }

  /**
   * Tab label text mirrors each panel's `data-tab-label` (set server-side on
   * the panel, where Liquid reliably resolves the block's settings) instead
   * of being computed a second time in the tablist's own render, which
   * reads section.blocks[].settings outside of content_for 'blocks' and
   * comes back blank on this theme/API version.
   */
  #syncTabLabels() {
    const { tabLabels, panels } = this.refs;

    for (const [i, label] of tabLabels.entries()) {
      const text = panels[i]?.dataset.tabLabel;
      if (text) label.textContent = text;
    }
  }

  /**
   * Declarative handler for `on:click="/select/{index}"` on each tab.
   * @param {number} index
   */
  select(index) {
    this.#activate(Number(index));
  }

  /**
   * Declarative handler for `on:keydown="/handleKeydown"` on the tablist.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    const i = tabs.indexOf(target);
    if (i === -1) return;

    const offsets = { ArrowLeft: -1, ArrowRight: 1, Home: -i, End: tabs.length - 1 - i };
    const offset = offsets[/** @type {keyof typeof offsets} */ (event.key)];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (i + offset + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    this.#activate(nextIndex);
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const { tabs, panels } = this.refs;
    if (index === this.#active || index < 0 || index >= tabs.length) return;

    this.#active = index;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      const isActive = i === index;
      panel.toggleAttribute('inert', !isActive);
      panel.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  }
}

if (!customElements.get('featured-products-component')) {
  customElements.define('featured-products-component', FeaturedProductsComponent);
}
